import {
  platformNameConverter,
  localeHasDateFormat,
} from '../common-utilities';

import type { IAppDetailsAppData, IPlatform } from '../common-types';
import { styled } from '@mui/material';
import { InformationList } from '../AppDetails';

const Container = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(3)} 0`,
}));

export type PlatformInformationSliceProps = IPlatform & {
  app: IAppDetailsAppData;
  locale: string;
  translatedText: {
    platformInformationSliceTitle: string;
    platformInformationSliceDeveloperLabel: string;
    platformInformationSliceSizeLabel: string;
    platformInformationSliceCountryLabel: string;
    platformInformationSliceLastUpdatedLabel: string;
    platformInformationSliceAppVersionLabel: string;
    platformInformationSliceOrchaReviewDateLabel: string;
  };
};

/**
 * PlatformInformationSlice from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param locale is required, default to "en-GB" if not provided
 * @param translatedText is required, this is the text that as already been translated by the consumer of this component.
 */

export const PlatformInformationSlice = ({
  platformName,
  locale = 'en-GB',
  app,
  translatedText,
  ...props
}: PlatformInformationSliceProps) => {
  const { platforms } = app ?? {};
  const relevantPlatform = platforms?.find(
    (p) => platformNameConverter(p.platformName) === platformName
  );
  const platformData = relevantPlatform ?? undefined;

  return (
    <Container {...props}>
      <InformationList
        title={translatedText?.platformInformationSliceTitle}
        items={[
          {
            label: translatedText?.platformInformationSliceDeveloperLabel,
            value: platformData?.developerName,
          },
          {
            label: translatedText?.platformInformationSliceSizeLabel,
            value: platformData?.fileSize,
          },
          {
            label: translatedText?.platformInformationSliceCountryLabel,
            value: platformData?.country,
          },
          {
            label: translatedText?.platformInformationSliceLastUpdatedLabel,
            value: platformData?.lastUpdatedDate
              ? new Date(platformData?.lastUpdatedDate).toLocaleDateString([
                  localeHasDateFormat(locale) ? (locale ?? 'en-GB') : 'en-GB',
                  'en-GB',
                ])
              : undefined,
          },
          {
            label: translatedText?.platformInformationSliceAppVersionLabel,
            value: platformData?.appVersion,
          },
          {
            label: translatedText?.platformInformationSliceOrchaReviewDateLabel,
            value: platformData?.orchaReviewDate
              ? new Date(platformData?.orchaReviewDate).toLocaleDateString([
                  localeHasDateFormat(locale) ? (locale ?? 'en-GB') : 'en-GB',
                  'en-GB',
                ])
              : undefined,
          },
        ]}
      />
    </Container>
  );
};
